import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Navigate, useNavigate } from "react-router-dom";
import { Fingerprint, Search, X, Plus, Mail, Camera, ImagePlus } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";
import { maskIdentity } from "lib/identity";

const KINDS = ["email", "cpf", "phone", "discord", "external"];

function Chip({ children, tone }) {
  const cls = tone === "green" ? "border-emerald-500/40 text-emerald-500"
    : tone === "red" ? "border-red-500/40 text-red-500"
    : "border-border text-muted-foreground";
  return <span className={`px-2 py-0.5 rounded-md border text-xs ${cls}`}>{children}</span>;
}

export default function Biometrics() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const fileRef = useRef(null);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [results, setResults] = useState([]);
  const [queryFace, setQueryFace] = useState("");
  const [prefilter, setPrefilter] = useState(null);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/biometric-people/?q=${encodeURIComponent(query || "")}`);
      setRows(res.data.results || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load("");
    api.get("/api/companies/").then((r) => setCompanies(r.data.results || r.data || [])).catch(() => {});
  }, [load]);

  const onPhoto = async (file) => {
    if (!file) return;
    setSearching(true); setSearchErr("");
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const res = await api.post("/api/biometric-people/search/", fd);
      setResults(res.data.results || []);
      setQueryFace(res.data.query_face || "");
      setPrefilter(res.data.prefilter || null);
      setSearchMode(true);
    } catch (e) {
      setSearchErr(e?.response?.status === 422
        ? t("bio.errNoFace", "No usable face in the uploaded photo.")
        : (e?.response?.data?.detail || t("bio.errSearchFailed", "Photo search failed.")));
    } finally {
      setSearching(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const clearSearch = () => { setSearchMode(false); setResults([]); setQueryFace(""); setPrefilter(null); };

  if (user && !user.capabilities?.manage_system) return <Navigate to="/liveness/sessions" replace />;

  const displayRows = searchMode ? results : rows;

  return (
    <div className="animate-fade-in w-full">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Fingerprint className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">{t("bio.title", "Biometric identities")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => onPhoto(e.target.files?.[0])} />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={searching}>
            <Camera className="h-4 w-4 mr-1" />{searching ? t("bio.searching", "Searching…") : t("bio.searchByPhoto", "Search by photo")}
          </Button>
          <Button size="sm" onClick={() => setShowNew(true)}>
            <Plus className="h-4 w-4 mr-1" />{t("bio.newPerson", "New enrollment link")}
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-5">{t("bio.subtitle", "Global biometric people, linked identities, per-company consent, enrollment & LGPD.")}</p>

      {!searchMode && (
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
            placeholder={t("bio.search", "Search by name or identity…")}
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-background text-sm" />
        </div>
      )}

      {searchErr && <p className="text-sm text-red-500 mb-3">{searchErr}</p>}
      {searchMode && (
        <div className="mb-4 flex items-center gap-3">
          {queryFace && <img src={queryFace} alt="query" className="h-14 w-14 rounded-lg object-cover border border-border bg-black" />}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ImagePlus className="h-4 w-4" />
              {t("bio.sortedBySimilarity", "Ranked by facial similarity")} · {results.length} {t("bio.matches", "match(es)")}
              <button onClick={clearSearch} className="inline-flex items-center gap-1 text-primary hover:underline">
                <X className="h-3.5 w-3.5" /> {t("bio.clear", "clear")}
              </button>
            </div>
            {prefilter && prefilter.enabled && (
              <div className="text-xs text-muted-foreground/80 font-mono">
                {t("bio.prefilterOn", "Pre-filter")}: {prefilter.analyzed?.toLocaleString()} / {prefilter.total?.toLocaleString()} {t("bio.analyzed", "analyzed")}
                {prefilter.speedup ? ` · ${prefilter.speedup}× ${t("bio.fewer", "fewer exact compares")}` : ""}
              </div>
            )}
            {prefilter && !prefilter.enabled && prefilter.total > 0 && (
              <div className="text-xs text-muted-foreground/70 font-mono">
                {t("bio.prefilterOff", "Full scan")}: {prefilter.total?.toLocaleString()} {t("bio.records", "records")} ({t("bio.prefilterUntrained", "pre-filter not trained yet")})
              </div>
            )}
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  {searchMode && <th className="text-left px-4 py-2 font-medium">{t("bio.similarity", "Similarity")}</th>}
                  <th className="text-left px-4 py-2 font-medium">{t("bio.name", "Name")}</th>
                  <th className="text-left px-4 py-2 font-medium">{t("bio.identities", "Identities")}</th>
                  <th className="text-left px-4 py-2 font-medium">{t("bio.enrolled", "Enrolled")}</th>
                  <th className="text-left px-4 py-2 font-medium">{t("bio.companies", "Companies")}</th>
                  <th className="text-left px-4 py-2 font-medium">{t("bio.status", "Status")}</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/liveness/admin/biometrics/${p.id}`)}
                    className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer">
                    {searchMode && (
                      <td className="px-4 py-3">
                        <Chip tone={p.similarity >= 0.5 ? "green" : "red"}>{Math.round((p.similarity || 0) * 100)}%</Chip>
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium">{p.name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">
                      {p.identities.map((i, k) => <Chip key={k} title={i.type}>{maskIdentity(i.type, i.value)}</Chip>)}
                    </div></td>
                    <td className="px-4 py-3">{p.enrolled ? <Chip tone="green">{t("bio.yes", "Yes")}</Chip> : <Chip>{t("bio.no", "No")}</Chip>}</td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">
                      {p.companies.map((c, k) => <Chip key={k} tone={c.consent ? "green" : "red"}>{c.name}{c.consent ? " ✓" : " ✗"}</Chip>)}
                    </div></td>
                    <td className="px-4 py-3"><div className="flex items-center gap-1.5">
                      {p.key_removed ? <Chip tone="red">{t("bio.purged", "Purged (LGPD)")}</Chip> : <Chip tone="green">{p.status}</Chip>}
                      {p.tier === "gold" && <Chip tone="green">★ {t("bio.gold", "Ouro")}</Chip>}
                      {p.tier === "silver" && <Chip>{t("bio.silver", "Prata")}</Chip>}
                      {p.disabled && <Chip tone="red">{t("bio.disabled", "Desabilitada")}</Chip>}
                      {p.blacklist_mode === "deny" && <Chip tone="red">deny</Chip>}
                    </div></td>
                  </tr>
                ))}
                {!displayRows.length && !loading && !searching && (
                  <tr><td colSpan={searchMode ? 6 : 5} className="text-center py-10 text-muted-foreground">
                    {searchMode ? t("bio.noMatches", "No similar people found.") : t("bio.empty", "No biometric people yet.")}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showNew && createPortal(
        <NewEnrollModal t={t} companies={companies} onClose={() => setShowNew(false)}
          onDone={() => load(q)} />, document.body)}
    </div>
  );
}

function NewEnrollModal({ t, companies, onClose, onDone }) {
  const [name, setName] = useState("");
  const [itype, setItype] = useState("email");
  const [ival, setIval] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);   // {enrollment_url, qr, session_token, ...}
  const [copied, setCopied] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sent, setSent] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/api/biometric-people/enroll-link/", {
        name: name.trim(), identity_type: itype, identity_value: ival.trim(), company_id: companyId,
      });
      setResult(r.data); setSendTo(itype === "email" ? ival.trim() : ""); onDone?.();
    } catch (e) { setErr(e?.response?.data?.detail || String(e)); } finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(result.enrollment_url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const sendEmail = async () => {
    setErr(""); setSent("");
    try {
      await api.post("/api/biometric-people/enroll-link/send/", { session_token: result.session_token, email: sendTo.trim() });
      setSent(sendTo.trim());
    } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">{t("bio.newPerson", "New enrollment link")}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        {!result ? (
          <div className="space-y-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("bio.name", "Name")}
              className="w-full h-10 rounded-lg border border-border bg-background text-sm px-3" />
            <div className="flex gap-2">
              <select value={itype} onChange={(e) => setItype(e.target.value)}
                className="h-10 rounded-lg border border-border bg-background text-sm px-2">
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <input value={ival} onChange={(e) => setIval(e.target.value)} placeholder={t("bio.identityValue", "value")}
                className="flex-1 h-10 rounded-lg border border-border bg-background text-sm px-3" />
            </div>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}
              className="w-full h-10 rounded-lg border border-border bg-background text-sm px-2">
              <option value="">{t("bio.company", "Select company…")}</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {err && <p className="text-sm text-red-500">{err}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={onClose}>{t("bio.cancel", "Cancel")}</Button>
              <Button size="sm" disabled={busy || !ival.trim() || !companyId} onClick={submit}>
                {busy ? t("bio.creating", "Creating…") : t("bio.createLink", "Create link")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("bio.linkReady", "Share this link with the person to enroll their biometrics:")}</p>
            {result.qr && (
              <div className="flex justify-center">
                <img src={result.qr} alt="QR" className="w-40 h-40 rounded-lg border border-border bg-white p-2" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <input readOnly value={result.enrollment_url}
                className="flex-1 h-10 rounded-lg border border-border bg-muted text-sm px-3 font-mono" />
              <Button size="sm" variant="outline" onClick={copy}>{copied ? t("bio.copied", "Copied!") : t("bio.copy", "Copy")}</Button>
            </div>
            {/* Send / resend by e-mail */}
            <div className="flex items-center gap-2">
              <input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)}
                placeholder={t("bio.sendEmail", "Send to e-mail…")}
                className="flex-1 h-10 rounded-lg border border-border bg-background text-sm px-3" />
              <Button size="sm" variant="outline" disabled={!sendTo.trim()} onClick={sendEmail}>
                <Mail className="h-4 w-4 mr-1" />{sent ? t("bio.sent", "Sent!") : t("bio.send", "Send")}
              </Button>
            </div>
            {err && <p className="text-sm text-red-500">{err}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <a href={result.enrollment_url} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline">{t("bio.openLink", "Open")}</Button>
              </a>
              <Button size="sm" onClick={onClose}>{t("bio.done", "Done")}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
